import { isSiteCanadian } from 'src/utils/helpers';
import { Permissions } from 'src/utils/enums/permissions.enum';
import { useLocalization } from 'src/utils/useLocalization';
import { useLocation } from 'react-router-dom';

import { useTranslation } from 'react-i18next';
import React, {
    Dispatch,
    SetStateAction,
    useEffect,
    useMemo,
    useState,
} from 'react';
import './styles.scss';

enum EStatus {
    ACTIVE = 'Active',
    INACTIVE = 'Inactive',
}

const INACTIVE_DEALER_PERMISSIONS = [
    Permissions.USER_ADMIN,
    Permissions.PAYOFF_QUOTES,
    Permissions.ASSIGN_MANAGE_SALES_MATURITIES,
    Permissions.VIEW_MATURITIES,
];

enum ECountries {
    US = 'US',
    CA = 'CA',
}
interface IAccordionProps {
    dealerId: string;
    dealerName?: string;
    permissionsMap: Map<string, Array<string>>;
    setPermissionsMap: Dispatch<SetStateAction<Map<string, Array<string>>>>;
    disabled: boolean;
    isDSMSearch?: boolean;
    isOpenDefault?: boolean;
    permissionsList?: Array<string> | null;
    newPermissionList?: Array<any> | null;
    status?: EStatus.ACTIVE | EStatus.INACTIVE | string;
    country?: ECountries.US | ECountries.CA | string;
    manufacturerCode?: string | number;
}

interface ICheckboxProps {
    name: string;
    value: string;
    dealerId: string;
    isDSMSearch?: boolean;
    permissionsMap: Map<string, Array<string>>;
    setPermissionsMap: Dispatch<SetStateAction<Map<string, Array<string>>>>;
    status?: EStatus.ACTIVE | EStatus.INACTIVE | string;
}

interface IPermissionPickerProps {
    visibleClass: string;
    dealerId: string;
    isDSMSearch?: boolean;
    permissionsMap: Map<string, Array<string>>;
    setPermissionsMap: Dispatch<SetStateAction<Map<string, Array<string>>>>;
    permissionsList?: Array<string> | null;
    addPermissionsList?: Array<{ name: string; value: Permissions }> | null;
    status?: EStatus.ACTIVE | EStatus.INACTIVE | string;
    country?: ECountries.US | ECountries.CA | string;
    manufacturerCode?: string | number;
}

interface IDealerPaymentProps {
    dealerId: string;
    permissionsMap: Map<string, Array<string>>;
    setPermissionsMap: Dispatch<SetStateAction<Map<string, Array<string>>>>;
    permissionsList?: Array<string> | null;
}

const directPermissions = [
    Permissions.VOLKSWAGEN_DIRECT,
    Permissions.AUDI_DIRECT,
    Permissions.VW_AUDI_DIRECT,
];

const isDirectPermissionValue = (permissionValue: string): boolean =>
    directPermissions.some((directPermission) =>
        permissionValue.includes(directPermission),
    );

const hasAnyDirectPermission = (permissions: string[]): boolean =>
    permissions.some((permission) =>
        directPermissions.some((directPermission) =>
            permission.includes(directPermission),
        ),
    );

const isSelected = (
    value: string,
    dealerId: string,
    permissionsMap: Map<string, string[]>,
): boolean => {
    const mapItem = permissionsMap.get(dealerId);

    if (!mapItem?.length) {
        return false;
    }

    if (mapItem.includes(value)) {
        return true;
    }

    if (isDirectPermissionValue(value)) {
        return hasAnyDirectPermission(mapItem);
    }

    return false;
};

const DealerPayment: React.FC<IDealerPaymentProps> = ({
    dealerId,
    permissionsMap,
    setPermissionsMap,
    permissionsList,
}) => {
    const { addNewUser } = useLocalization();
    const [isSubmitDisable, setIsSubmitDisable] = useState<boolean>(false);
    const [isSubmitApproveChecked, setIsSubmitApproveChecked] =
        useState<boolean>(
            isSelected(
                Permissions.DEALER_ELECTRONIC_PAYMENT_SUBMIT_AND_APPROVE,
                dealerId,
                permissionsMap,
            ),
        );
    const [isSubmitChecked, setIsSubmitChecked] = useState<boolean>(
        isSelected(
            Permissions.DEALER_ELECTRONIC_PAYMENT_SUBMIT,
            dealerId,
            permissionsMap,
        ),
    );
    const canSubmit =
        !permissionsList ||
        permissionsList?.includes(
            Permissions.DEALER_ELECTRONIC_PAYMENT_SUBMIT,
        ) ||
        permissionsList?.includes(Permissions.DEALER_ELECTRONIC_PAYMENT);
    const canSubmitApprove =
        !permissionsList ||
        permissionsList?.includes(
            Permissions.DEALER_ELECTRONIC_PAYMENT_SUBMIT_AND_APPROVE,
        ) ||
        permissionsList?.includes(Permissions.DEALER_ELECTRONIC_PAYMENT);

    const onChangeHandler = (event: any) => {
        const jump = 1;
        const permissions = permissionsMap.get(dealerId);
        const value = event.currentTarget.value;
        if (event.currentTarget.checked) {
            permissions?.push(value);
            if (
                value ==
                Permissions.DEALER_ELECTRONIC_PAYMENT_SUBMIT_AND_APPROVE
            ) {
                if (
                    permissions?.findIndex(
                        (e) =>
                            e === Permissions.DEALER_ELECTRONIC_PAYMENT_SUBMIT,
                    ) === Number('-1')
                ) {
                    permissions?.push(
                        Permissions.DEALER_ELECTRONIC_PAYMENT_SUBMIT,
                    );
                }
                setIsSubmitApproveChecked(true);
                setIsSubmitChecked(true);
            } else {
                setIsSubmitChecked(true);
            }
        } else {
            permissions?.splice(
                permissions.findIndex((p) => p == value),
                jump,
            );
            if (
                value ==
                Permissions.DEALER_ELECTRONIC_PAYMENT_SUBMIT_AND_APPROVE
            ) {
                setIsSubmitApproveChecked(false);
                setIsSubmitChecked(false);
            } else {
                setIsSubmitChecked(false);
            }
        }
        setPermissionsMap(new Map(permissionsMap));
    };

    useEffect(() => {
        const isSubmitApproveSelected = isSelected(
            Permissions.DEALER_ELECTRONIC_PAYMENT_SUBMIT_AND_APPROVE,
            dealerId,
            permissionsMap,
        );
        setIsSubmitApproveChecked(isSubmitApproveSelected);
        setIsSubmitChecked(
            isSelected(
                Permissions.DEALER_ELECTRONIC_PAYMENT_SUBMIT,
                dealerId,
                permissionsMap,
            ),
        );
        setIsSubmitDisable(isSubmitApproveSelected);
    }, [permissionsMap]);

    if (permissionsList && !canSubmit && !canSubmitApprove) {
        return null;
    }

    return (
        <>
            <span className='title'>
                {
                    addNewUser.DEALERSHIP_SELECT_PERMISSION_TITLE_ELECTRONIC_PAYMENTS
                }
            </span>
            {(canSubmit || canSubmitApprove) && (
                <label className='c-checkbox'>
                    <input
                        className='c-checkbox__input'
                        type='checkbox'
                        value={Permissions.DEALER_ELECTRONIC_PAYMENT_SUBMIT}
                        onChange={onChangeHandler}
                        checked={isSubmitChecked}
                        disabled={isSubmitDisable}
                    />
                    <span className='c-checkbox__label'>
                        {
                            addNewUser.DEALERSHIP_SELECT_PERMISSION_SUBMIT_ELECTRONIC_PAYMENT
                        }
                    </span>
                </label>
            )}
            {(canSubmitApprove || canSubmit) && (
                <label className='c-checkbox'>
                    <input
                        className='c-checkbox__input'
                        type='checkbox'
                        value={
                            Permissions.DEALER_ELECTRONIC_PAYMENT_SUBMIT_AND_APPROVE
                        }
                        onChange={onChangeHandler}
                        checked={isSubmitApproveChecked}
                    />
                    <span className='c-checkbox__label'>
                        {
                            addNewUser.DEALERSHIP_SELECT_PERMISSION_SUBMIT_APPROVED_ELECTRONIC_PAYMENT
                        }
                    </span>
                </label>
            )}
        </>
    );
};

const Checkbox: React.FC<ICheckboxProps> = ({
    name,
    value,
    dealerId,
    permissionsMap,
    setPermissionsMap,
    status,
}) => {
    const [initiallyChecked] = useState<boolean>(
        isSelected(value, dealerId, permissionsMap),
    );

    const [isChecked, setIsChecked] = useState<boolean>(initiallyChecked);

    const isInactive = status && status === EStatus.INACTIVE;
    const canChange = (status && status === EStatus.ACTIVE) || !status;

    const onChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        const permissions = permissionsMap.get(dealerId) ?? [];
        const updatedPermissionsMap = new Map(permissionsMap);

        if (event.currentTarget.checked) {
            if (!permissions.includes(value)) {
                updatedPermissionsMap.set(dealerId, [...permissions, value]);
            }

            setIsChecked(true);
        } else {
            const updatedPermissions = isDirectPermissionValue(value)
                ? permissions.filter(
                      (permission) => !isDirectPermissionValue(permission),
                  )
                : permissions.filter((permission) => permission !== value);

            updatedPermissionsMap.set(dealerId, updatedPermissions);
            setIsChecked(false);
        }

        setPermissionsMap(updatedPermissionsMap);
    };

    useEffect(() => {
        setIsChecked(isSelected(value, dealerId, permissionsMap));
    }, [permissionsMap, value, dealerId]);

    return (
        <label className={'c-checkbox'}>
            <input
                className='c-checkbox__input'
                type='checkbox'
                value={value}
                disabled={isInactive ? true : false}
                onChange={
                    canChange
                        ? onChangeHandler
                        : () => {
                              return null;
                          }
                }
                checked={isInactive ? true : isChecked}
            />
            <span className='c-checkbox__label'>{name}</span>
        </label>
    );
};

const PermissionsPicker: React.FC<IPermissionPickerProps> = ({
    visibleClass,
    dealerId,
    permissionsMap,
    setPermissionsMap,
    permissionsList,
    addPermissionsList,
    isDSMSearch = false,
    status,
    country,
    manufacturerCode,
}) => {
    const { addNewUser } = useLocalization();
    const { i18n } = useTranslation();
    const { pathname } = useLocation();
    const hasDealerGroup = pathname.includes('dealer-group-maintenance');
    const lang = i18n?.language?.includes('fr') ? 'fr' : 'en';
    let permissionsListUpdated: Array<string> = [];

    useEffect(() => {
        if (status !== EStatus.INACTIVE) return;
        const current = permissionsMap.get(dealerId) ?? [];
        const alreadySet =
            current.length === INACTIVE_DEALER_PERMISSIONS.length &&
            INACTIVE_DEALER_PERMISSIONS.every((p) => current.includes(p));
        if (alreadySet) return;
        const updated = new Map(permissionsMap);
        updated.set(dealerId, [...INACTIVE_DEALER_PERMISSIONS]);
        setPermissionsMap(updated);
        // permissionsMap intentionally omitted: effect should only run when status/dealerId changes
    }, [status, dealerId, setPermissionsMap]);
    const permissions = useMemo(() => {
        let basePermissions = [
            {
                name: addNewUser.DEALERSHIP_SELECT_PERMISSION_ADMIN,
                value: Permissions.USER_ADMIN,
            },
            {
                name: addNewUser.DEALERSHIP_SELECT_PERMISSION_CREDITAPPLICATION,
                value: Permissions.CREDIT_APPLICATION,
            },
            {
                name: addNewUser.DEALERSHIP_SELECT_PERMISSION_APPLICATIONSTATUS,
                value: Permissions.APPLICATION_STATUS_TRACKING,
            },
            {
                name: addNewUser.DEALERSHIP_SELECT_PERMISSION_FUNDINGSTATUS,
                value: Permissions.FUNDING_STATUS,
            },
            {
                name: addNewUser.DEALERSHIP_SELECT_PERMISSION_PAYOFFQUOTES,
                value: Permissions.PAYOFF_QUOTES,
            },
            {
                name: addNewUser.DEALERSHIP_SELECT_PERMISSION_BULLETINS,
                value: Permissions.MARKETING_BULLETINS,
            },
            {
                name: addNewUser.DEALERSHIP_SELECT_PERMISSION_DEALERRESERVEEFT,
                value: Permissions.DEALER_RESERVES_EFT_NOTIFICATIONS,
            },
        ];

        if (addPermissionsList && addPermissionsList.length) {
            basePermissions = [...basePermissions, ...addPermissionsList];
        }

        if (
            (isDSMSearch && country && country === ECountries.US) ||
            !isSiteCanadian()
        ) {
            basePermissions.push(
                {
                    name: addNewUser.DEALERSHIP_SELECT_PERMISSION_DEALERRENTAL,
                    value: Permissions.DEALER_RENTAL,
                },
                {
                    name: addNewUser.DEALERSHIP_SELECT_PERMISSION_SERVICELOANER,
                    value: Permissions.SERVICE_LOANER,
                },
            );

            if (manufacturerCode) {
                if (+manufacturerCode === Number('71')) {
                    basePermissions.push({
                        name: 'Audi Direct',
                        value: Permissions.VW_AUDI_DIRECT,
                    });
                } else if (+manufacturerCode === Number('70')) {
                    basePermissions.push({
                        name: 'VW Direct',
                        value: Permissions.VW_AUDI_DIRECT,
                    });
                }
            } else {
                basePermissions.push({
                    name: 'Audi/VW Direct',
                    value: Permissions.VW_AUDI_DIRECT,
                });
            }
        }

        if (!permissionsList?.length) return basePermissions;

        if (!hasDealerGroup && status && status === EStatus.INACTIVE) {
            permissionsListUpdated = [...INACTIVE_DEALER_PERMISSIONS];

            return basePermissions.filter(
                (permission) =>
                    !permissionsListUpdated ||
                    permissionsListUpdated.includes(permission.value),
            );
        }

        permissionsListUpdated = [...permissionsList];

        return basePermissions.filter(
            (permission) =>
                !permissionsListUpdated ||
                permissionsListUpdated.includes(permission.value),
        );
    }, [lang, permissionsList, permissionsListUpdated]);

    let hasMaturityManagePermission =
        !permissionsListUpdated ||
        permissionsListUpdated.includes(
            Permissions.ASSIGN_MANAGE_SALES_MATURITIES,
        );

    let hasMaturityViewPermission =
        !permissionsListUpdated ||
        permissionsListUpdated.includes(Permissions.VIEW_MATURITIES);

    let hasQuickFundManagePermission =
        !permissionsListUpdated ||
        permissionsListUpdated.includes(Permissions.QUICK_FUND_SAVE);

    let hasQuickFundViewPermission =
        !permissionsListUpdated ||
        permissionsListUpdated.includes(Permissions.QUICK_FUND_VIEW);

    let showMaturityPermissions =
        !permissionsListUpdated ||
        hasMaturityManagePermission ||
        hasMaturityViewPermission;

    let showDealerPaymentPermissions =
        !permissionsListUpdated ||
        permissionsListUpdated.some(
            (permission) =>
                permission === Permissions.DEALER_ELECTRONIC_PAYMENT_SUBMIT ||
                permission ===
                    Permissions.DEALER_ELECTRONIC_PAYMENT_SUBMIT_AND_APPROVE,
        );

    let showQuickFundpermissions =
        !permissionsListUpdated ||
        permissionsListUpdated.some(
            (permission) =>
                permission === Permissions.QUICK_FUND_VIEW ||
                permission === Permissions.QUICK_FUND_SAVE,
        );

    let showMaturity = permissionsMap
        ?.get(dealerId)
        ?.some(
            (permission) =>
                permission === Permissions.ASSIGN_MANAGE_SALES_MATURITIES ||
                permission === Permissions.VIEW_MATURITIES,
        );

    let showPayment = permissionsMap
        ?.get(dealerId)
        ?.some(
            (permission) =>
                permission === Permissions.DEALER_ELECTRONIC_PAYMENT_SUBMIT ||
                permission ===
                    Permissions.DEALER_ELECTRONIC_PAYMENT_SUBMIT_AND_APPROVE,
        );

    let showQuickFund = permissionsMap
        ?.get(dealerId)
        ?.some(
            (permission) =>
                permission === Permissions.QUICK_FUND_VIEW ||
                permission === Permissions.QUICK_FUND_SAVE,
        );

    if (isDSMSearch) {
        const flagMaturity = permissionsListUpdated!.some(
            (permission) => permission === Permissions.MATURITY_MANAGER,
        );
        if (flagMaturity) {
            showMaturity = true;
            showMaturityPermissions = true;
            hasMaturityManagePermission = true;
            hasMaturityViewPermission = true;
        }

        const flagPayment = permissionsListUpdated!.some(
            (permission) =>
                permission === Permissions.DEALER_ELECTRONIC_PAYMENT,
        );
        if (flagPayment) {
            showPayment = true;
            showDealerPaymentPermissions = true;
        }

        const flagQuick = permissionsListUpdated!.some(
            (permission) => permission === Permissions.QUICK_FUND,
        );
        if (flagQuick) {
            showQuickFund = true;
            showQuickFundpermissions = true;
            hasQuickFundViewPermission = true;
            hasQuickFundManagePermission = true;
        }
    }

    return (
        <div className={`dealership-dealer-permissions-picker ${visibleClass}`}>
            <div className='permissions-picker-box1'>
                {permissions.map((permission) => {
                    return (
                        <Checkbox
                            name={permission.name}
                            value={permission.value}
                            key={`permission-${permission.name}`}
                            dealerId={dealerId}
                            permissionsMap={permissionsMap}
                            setPermissionsMap={setPermissionsMap}
                            isDSMSearch={isDSMSearch}
                            status={status}
                        />
                    );
                })}
            </div>
            <div className='permissions-picker-box2'>
                {(showMaturityPermissions || showMaturity) && (
                    <>
                        {(hasMaturityManagePermission ||
                            hasMaturityViewPermission) && (
                            <span className='title'>
                                {
                                    addNewUser.DEALERSHIP_SELECT_PERMISSION_TITLE_MATURITY
                                }
                            </span>
                        )}
                        {(hasMaturityManagePermission ||
                            hasMaturityViewPermission) && (
                            <Checkbox
                                name={
                                    addNewUser.DEALERSHIP_SELECT_PERMISSION_MATURITY_ASSIGN
                                }
                                value={
                                    Permissions.ASSIGN_MANAGE_SALES_MATURITIES
                                }
                                dealerId={dealerId}
                                permissionsMap={permissionsMap}
                                setPermissionsMap={setPermissionsMap}
                                status={status}
                            />
                        )}
                        {(hasMaturityViewPermission ||
                            hasMaturityManagePermission) && (
                            <Checkbox
                                name={
                                    addNewUser.DEALERSHIP_SELECT_PERMISSION_MATURITY_VIEW
                                }
                                value={Permissions.VIEW_MATURITIES}
                                dealerId={dealerId}
                                permissionsMap={permissionsMap}
                                setPermissionsMap={setPermissionsMap}
                                status={status}
                            />
                        )}
                    </>
                )}
                {((isDSMSearch && country && country === ECountries.US) ||
                    !isSiteCanadian()) &&
                    (showQuickFund || showQuickFundpermissions) && (
                        <>
                            {hasQuickFundManagePermission ||
                            hasQuickFundViewPermission ? (
                                <span className='title'>
                                    {
                                        addNewUser.DEALERSHIP_SELECT_PERMISSION_TITLE_QUICKFUND
                                    }
                                </span>
                            ) : null}
                            {(hasQuickFundManagePermission ||
                                hasQuickFundViewPermission) && (
                                <Checkbox
                                    name={
                                        addNewUser.DEALERSHIP_SELECT_PERMISSION_QUICKFUND_ASSIGN
                                    }
                                    value={Permissions.QUICK_FUND_SAVE}
                                    dealerId={dealerId}
                                    permissionsMap={permissionsMap}
                                    setPermissionsMap={setPermissionsMap}
                                    status={status}
                                />
                            )}
                            {(hasQuickFundViewPermission ||
                                hasQuickFundManagePermission) && (
                                <Checkbox
                                    name={
                                        addNewUser.DEALERSHIP_SELECT_PERMISSION_QUICKFUND_VIEW
                                    }
                                    value={Permissions.QUICK_FUND_VIEW}
                                    dealerId={dealerId}
                                    permissionsMap={permissionsMap}
                                    setPermissionsMap={setPermissionsMap}
                                    status={status}
                                />
                            )}
                        </>
                    )}
                {((isDSMSearch && country && country === ECountries.CA) ||
                    isSiteCanadian()) &&
                showDealerPaymentPermissions &&
                showPayment ? (
                    <DealerPayment
                        dealerId={dealerId}
                        permissionsMap={permissionsMap}
                        setPermissionsMap={setPermissionsMap}
                        permissionsList={permissionsList}
                    />
                ) : null}
            </div>
        </div>
    );
};

const Accordion: React.FC<IAccordionProps> = ({
    dealerId,
    dealerName,
    permissionsMap,
    setPermissionsMap,
    disabled,
    permissionsList,
    newPermissionList,
    isOpenDefault,
    isDSMSearch = false,
    status,
    country,
    manufacturerCode,
}) => {
    const [isOpen, setIsOpen] = useState<boolean>(isOpenDefault ?? true);
    const expandedClass = isOpen
        ? 'c-icon--[semantic-collapse]'
        : 'c-icon--[semantic-expand]';

    const onClickHeaderHandler = () => {
        if (!disabled) setIsOpen(!isOpen);
    };

    const isVisibleClass = isOpen ? 'visible' : 'hide';
    const disabledClass = disabled ? ' disabled' : '';

    useEffect(() => {
        if (disabled) setIsOpen(false);
    }, [disabled]);

    return (
        <div className='new-user-container__body-dealership-dealer-permissions-container'>
            <div
                className={
                    'dealership-dealer-permissions-header' + disabledClass
                }
                onClick={onClickHeaderHandler}
            >
                <span className='dealership-dealer-permissions-select'>
                    <h3
                        className={
                            'dealership-dealer-permissions-id' + disabledClass
                        }
                    >
                        {dealerId}
                    </h3>
                    {isDSMSearch ? (
                        <span
                            className={
                                'dealership-dealer-permissions-name' +
                                disabledClass
                            }
                        >
                            {status ? ` - (Status: ${status})` : null}
                        </span>
                    ) : null}
                    {dealerName ? (
                        <span
                            className={
                                'dealership-dealer-permissions-name' +
                                disabledClass
                            }
                        >
                            {status
                                ? ` ${dealerName}  - (Status: ${status})`
                                : ` ${dealerName} `}
                        </span>
                    ) : null}
                </span>
                <div
                    className={
                        'dealership-dealer-permissions-icon' + disabledClass
                    }
                >
                    <i
                        className={`c-icon c-icon--small ${expandedClass}`}
                        role='img'
                        aria-hidden='true'
                    ></i>
                </div>
            </div>
            <PermissionsPicker
                visibleClass={isVisibleClass}
                dealerId={dealerId}
                permissionsMap={permissionsMap}
                setPermissionsMap={setPermissionsMap}
                permissionsList={permissionsList}
                addPermissionsList={newPermissionList}
                isDSMSearch={isDSMSearch}
                status={status}
                country={country}
                manufacturerCode={manufacturerCode}
            />
        </div>
    );
};

export default Accordion;
